import { bookActions } from '@/actions';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { bookingsServices } from '@/features/bookings';
import { usePaymentMethodsContext } from '@/features/payments/hooks/usePaymentMethods';
import { paymentsServices } from '@/features/payments/payments.services';
import { SessionState, sessionStateSchema } from '@/features/payments/payments.types';
import { buildBokadirektPayReconcilePath, buildBokadirektPayReconcileUrl } from '@/features/payments/utils/paymentUrls';
import {
  filterEmptyValues,
  isAndroidPhone,
  isIPhone,
  isSistaminuten as isSistaminutenInstance,
  promiseWrapper,
  url,
} from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { createSession, mapApplePayLineItem } from '@/helpers/applepay';
import {
  buildBookingTrackingProps,
  getApplePaySummaryFeeItems,
  getApplePaySummaryGiftcardItems,
  getApplePaySummaryProductItems,
  getApplePaySummaryServiceItems,
  getBookingCheckoutEmployee,
  getBookingCheckoutFormFields,
  getBookingCheckoutSummary,
  getBookingRequestBody,
  getBookingRetryPathname,
  getBookingStartTime,
  getGooglePaySummaryFeeItems,
  getGooglePaySummaryGiftcardItems,
  getGooglePaySummaryProductItems,
  getGooglePaySummaryServiceItems,
  getGroupBookingParticipantSession,
  getUserProfileInfoFields,
  guestCheckoutManager,
  hasDealCampaign,
  hasSistaminutenCampaign,
  mapBookStateToCheckoutDetailsProducts,
  mapBookStateToCheckoutDetailsServices,
  saveBookingTrackingProps,
} from '@/helpers/checkout';
import {
  bookingFlowTrackingSession,
  getSummaryProperties,
  mapSelectedPaymentMethod,
} from '@/helpers/checkoutFlowTrackingSession';
import type { ContainmentMode } from '@/helpers/containmentPolicy';
import { getContainmentPolicy } from '@/helpers/containmentPolicy';
import { getEmbedContext, getEmbedThemeParams, isEmbedBundle, navigate, withEmbedBasename } from '@/helpers/embed';
import {
  buildBrowserInfo,
  clientInstance,
  getGoogleIsReadyToPayRequest,
  getGooglePayPaymentDataRequest,
} from '@/helpers/googlepay';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { useWidgetPayment, WidgetPaymentContextValue, WidgetPaymentState } from '@/hooks/useWidgetPayment';
import { bookingCheckoutFormDataSchema, CheckoutFormData, useCheckoutFormData } from '@/hooks/useCheckoutFormData';
import { _s, _t } from '@/locale';
import { adyenServices, placeService, swishServices, userService } from '@/services';
import { bookServices } from '@/services/bookServicesTs';
import { CofPaymentData } from '@/types/adyen';
import { CheckoutTracking } from '@/types/analytics/checkout';
import { submitAdditionalDetailsResponseSchema } from '@/types/api/services/adyen';
import {
  bookingCheckoutResultCodeResponseSchema,
  bookingCheckoutSessionResponseSchema,
  CofPaymentRequest,
  CofPaymentResponseSuccess,
  cofPaymentResponseSuccessSchema,
  CofPaymentResponseThreeDS,
  cofPaymentResponseThreeDSSchema,
  InitKlarnaResponse,
  initKlarnaResponseSchema,
  InitQliroResponse,
  initQliroResponseSchema,
  PaymentHistoryResponse,
  SaveBookingRequest,
  SaveBookingResponse,
  saveBookingResponseSchema,
  SwishPaymentBookingResponse,
  swishPaymentBookingResponseSchema,
  ValidateGiftCardRequest,
  ValidateGiftCardResponse,
} from '@/types/api/services/booking';
import { ErrorResponse, errorResponseSchema } from '@/types/api/services/schema';
import {
  availablePrePaymentMethodSchema,
  baseCheckoutStateSchema,
  BookingCheckoutSummary,
  bookingCheckoutSummarySchema,
  CheckoutAction,
  CheckoutAPI,
  CheckoutEmployee,
  CheckoutMissingAction,
  checkoutMissingActionSchema,
  CheckoutPaymentMethod,
  CheckoutSummary,
  SelectedPaymentMethod,
  selectedStoredCofPaymentMethodSchema,
  SubmitAdyenOptions,
} from '@/types/checkout';
import { PaymentCard } from '@/types/paymentcards';
import { BookState } from '@/types/state/book';
import { FormField, formFieldSchema } from '@/types/state/shared';
import * as Sentry from '@sentry/react';
import { Buffer } from 'buffer';
import type { History, UnregisterCallback } from 'history';
import { createContext, Dispatch, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { baseTranslationKey } from './BookingCheckout';

const swishStateSchema = z.object({
  payment: swishPaymentBookingResponseSchema.nullable(),
  notificationChannel: z.any().optional(),
  showQrCode: z.boolean().optional(),
});

type SwishState = z.infer<typeof swishStateSchema>;

type CofPaymentSuccessRedirectData = {
  paymentMethod:
    | typeof CHECKOUT_PAYMENT_METHOD.STORED_COF
    | typeof CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY
    | typeof CHECKOUT_PAYMENT_METHOD.APPLE_PAY;
  responseData: CofPaymentResponseSuccess;
};

type PayAtPlaceSuccessRedirectData = {
  paymentMethod: typeof CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE;
  responseData: SaveBookingResponse;
};

export type SwishSuccessRedirectData = {
  paymentMethod: typeof CHECKOUT_PAYMENT_METHOD.SWISH;
  responseData: SwishPaymentBookingResponse;
};

export type BokadirektPaySuccessRedirectData = {
  resourceId: number;
  resourceType: 'booking' | 'bundle';
};

type KlarnaSuccessRedirectData = {
  paymentMethod: typeof CHECKOUT_PAYMENT_METHOD.KLARNA;
  responseData: InitKlarnaResponse;
};

type QliroSuccessRedirectData = {
  paymentMethod: typeof CHECKOUT_PAYMENT_METHOD.QLIRO;
  responseData: InitQliroResponse;
};

export type SuccessRedirectData =
  | PayAtPlaceSuccessRedirectData
  | SwishSuccessRedirectData
  | KlarnaSuccessRedirectData
  | QliroSuccessRedirectData
  | CofPaymentSuccessRedirectData;

export const bookingCheckoutStateSchema = baseCheckoutStateSchema.merge(
  z.object({
    bookingId: z.string().nullish(),
    summary: bookingCheckoutSummarySchema,
    formFields: z.array(formFieldSchema),
    formData: bookingCheckoutFormDataSchema,
    swish: swishStateSchema.nullable().optional(),
    cofThreeDS: cofPaymentResponseThreeDSSchema.nullable().optional(),
    session: sessionStateSchema.nullable().optional(),
    termsAccepted: z.boolean(),
    missingActions: z.array(checkoutMissingActionSchema),
    guestId: z.string().optional(),
    protectedEmailError: z.boolean(),
    onlinePaymentRequiredEmail: z.boolean(),
    triggeredTurnstile: z.boolean().optional(),
    groupBooking: z.object({
      showModal: z.boolean(),
      participantId: z.string(),
      isUpdate: z.boolean(),
      hasAddedParticipant: z.boolean(),
    }),
  }),
);

export type BookingCheckoutState = z.infer<typeof bookingCheckoutStateSchema>;

type UseBookingCheckoutManagerResult = {
  employee: CheckoutEmployee;
  cofThreeDS?: CofPaymentResponseThreeDS | null;
  bookingCheckoutAPI: () => CheckoutAPI;
  onCloseAdyenCheckoutModal: () => void;
  onResetPayment: () => void;
  onSubmitting: () => void;
  onChangePaymentMethod: (method: SelectedPaymentMethod) => void;
  onApplyGiftcard: (
    code: string,
  ) => (
    callback: (
      props:
        | { success: true; data: ValidateGiftCardResponse['giftcard']; errorCode?: undefined }
        | { success: false; data: null; errorCode?: string },
    ) => void,
  ) => void;
  onRemoveGiftcard: (code: string) => () => void;
  onRemoveBundle: () => void;
  onToggleTerms: (checked: boolean) => void;
  onUseGuestCheckout: () => void;
  onDismissSwishQRCode: () => void;
  onUpdateGroupBooking: ({
    isUpdate,
    showModal,
    participantId,
    hasAddedParticipant,
  }: {
    isUpdate?: boolean;
    showModal?: boolean;
    participantId?: string;
    hasAddedParticipant?: boolean;
  }) => void;
  onTriggeredTurnstile: () => void;
  onCheckoutMissingAction: (action: CheckoutMissingAction) => void;
  onValidateEmailProtected: (email: string, onErrorCallback: () => void) => void;
  onValidateOnlinePaymentRequiredEmail: (placeId: number, email: string) => void;
  onDismissEmailProtectedError: () => void;
} & BookingCheckoutState;

export type SubmitBookingContext = {
  bookingRequestBody: SaveBookingRequest;
  bookingTrackingProps: CheckoutTracking.Booking;
  successCallback: ({ paymentMethod, responseData }: SuccessRedirectData) => void;
  errorCallback: (error: ErrorResponse, paymentMethod: CheckoutPaymentMethod) => void;
  abortCallback: (paymentMethod: CheckoutPaymentMethod) => void;
  bookState?: BookState;
};

type SubmitPayAtPlaceBookingContext = SubmitBookingContext & { fullyPaid?: boolean; turnstileToken?: string };
type InitQliroBookingContext = SubmitBookingContext;
type InitKlarnaBookingContext = SubmitBookingContext;
type SubmitCofBookingContext = SubmitBookingContext;
type SubmitGooglePayBookingContext = SubmitBookingContext & {
  services: google.payments.api.DisplayItem[];
  products: google.payments.api.DisplayItem[];
  giftcards: google.payments.api.DisplayItem[];
  fees: google.payments.api.DisplayItem[];
};
type SubmitApplePayBookingContext = SubmitBookingContext & {
  services: ApplePayJS.ApplePayLineItem[];
  products: ApplePayJS.ApplePayLineItem[];
  giftcards: ApplePayJS.ApplePayLineItem[];
  fees: ApplePayJS.ApplePayLineItem[];
};

type SubmitThreeDSContext = SubmitBookingContext & {
  cofThreeDS: CofPaymentResponseThreeDS;
  paymentMethod?:
    | typeof CHECKOUT_PAYMENT_METHOD.STORED_COF
    | typeof CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY
    | typeof CHECKOUT_PAYMENT_METHOD.APPLE_PAY;
};

type CompleteWalletBookingContext = SubmitBookingContext & {
  type: 'googlepay' | 'applepay';
  paymentData: CofPaymentData;
};

type SubmitSwishBookingContext = SubmitBookingContext & {
  swish?: SwishState;
};

type BookingCheckoutAction =
  | CheckoutAction<BookingCheckoutSummary>
  | {
      type: 'UPDATE_FORM_FIELDS';
      payload: FormField[];
    }
  | {
      type: 'SHOW_ADD_PARTICIPANT_MODAL';
      payload: boolean;
    }
  | {
      type: 'UPDATE_GROUP_BOOKING';
      payload: { showModal?: boolean; isUpdate?: boolean; participantId?: string; hasAddedParticipant?: boolean };
    }
  | {
      type: 'ONHOLD_COF';
      payload: {
        cofThreeDS: CofPaymentResponseThreeDS;
        bookingRequestBody: SaveBookingRequest;
        bookingTrackingProps: CheckoutTracking.Booking;
      };
    }
  | {
      type: 'ONHOLD_SWISH';
      payload:
        | (SwishState & {
            bookingRequestBody: SaveBookingRequest;
            bookingTrackingProps: CheckoutTracking.Booking;
          })
        | null;
    }
  | {
      type: 'ERROR_SWISH';
    }
  | {
      type: 'RESET_PAYMENT';
    }
  | {
      type: 'SET_PROTECTED_EMAIL_ERROR';
      payload: boolean;
    }
  | {
      type: 'CLOSE_SWISH_QRCODE_MODAL';
    }
  | {
      type: 'UPDATE_TERMS_ACCEPTED';
      payload: boolean;
    }
  | {
      type: 'SET_GUEST_ID';
    }
  | {
      type: 'SET_ONLINE_PAYMENT_REQUIRED_EMAIL';
      payload: boolean;
    }
  | {
      type: 'SET_TRIGGERED_TURNSTILE';
    }
  | {
      type: 'UPDATE_SESSION';
      payload: SessionState & { bookingId?: string; isRedirectFlow?: boolean };
    }
  | { type: 'REMOVE_SESSION' };

const CHECKOUT_STATE_LOCALSTORAGE_KEY_BASE = 'checkout-state-v2';

/**
 * Storage key for the persisted checkout state (survives full-page payment
 * redirects: Klarna/Swish/3DS).
 *
 * The out-of-frame pay popup is a top-level bokadirekt.se window and therefore
 * shares the first-party localStorage bucket with the standalone marketplace.
 * Namespacing the embed contexts keeps an embed payment's persisted redirect
 * state from colliding with an in-progress standalone checkout (and vice
 * versa). Standalone keeps the bare key — no migration for in-flight sessions.
 *
 * Write and read happen in the same context within one redirect lifecycle
 * (standalone→standalone, popup→popup), so the derivation stays consistent.
 */
export function getCheckoutStateStorageKey(): string {
  return getEmbedContext() === 'standalone'
    ? CHECKOUT_STATE_LOCALSTORAGE_KEY_BASE
    : `${CHECKOUT_STATE_LOCALSTORAGE_KEY_BASE}:embed`;
}

/**
 * Whether the checkout must hold its login card back behind a loading card
 * while a held token resolves, so a returning visitor never sees the guest card
 * flash before their logged-in state lands. Only contained: the marketplace has
 * resolved its session before checkout mounts, whereas the widget resolves a
 * stored token inside it. Which card a contained visitor then gets is the
 * widget's to decide — checkout renders whatever `WidgetLogin` supplies.
 */
export function isResolvingContainedSession({
  mode,
  hasToken,
  hasFetched,
}: {
  mode: ContainmentMode;
  hasToken: boolean;
  hasFetched: boolean;
}): boolean {
  return mode !== null && hasToken && !hasFetched;
}

export const EMBED_REDIRECT_METHODS = new Set<number>([
  CHECKOUT_PAYMENT_METHOD.KLARNA,
  CHECKOUT_PAYMENT_METHOD.SWISH,
  CHECKOUT_PAYMENT_METHOD.QLIRO,
  CHECKOUT_PAYMENT_METHOD.WALLEY,
  CHECKOUT_PAYMENT_METHOD.APPLE_PAY,
  CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY,
  CHECKOUT_PAYMENT_METHOD.NEW_COF,
]);

function isEmbedRedirectPaymentMethod(type: number): boolean {
  return EMBED_REDIRECT_METHODS.has(type);
}

export function saveBookingCheckoutStateToLocalStorage(state: BookingCheckoutState) {
  localStorage.setItem(getCheckoutStateStorageKey(), JSON.stringify({ savedCheckoutState: state }));
}

export function removeBookingCheckoutStateFromLocalStorage() {
  localStorage.removeItem(getCheckoutStateStorageKey());
}

/**
 * Reads the persisted checkout state for the current embed context and clears it, so a
 * redirect-validation mount consumes it exactly once. Shared by the Swish, 3DS and
 * Bokadirekt Pay redirect validators — each passes its own state shape as `T`.
 */
export function readAndClearSavedCheckoutState<T>(): T | null {
  try {
    const raw = localStorage.getItem(getCheckoutStateStorageKey());
    if (!raw) return null;
    const savedCheckoutState: T = JSON.parse(raw);
    removeBookingCheckoutStateFromLocalStorage();
    return savedCheckoutState;
  } catch {
    return null;
  }
}

/**
 * A pop the router cancels (`history.block`, as a widget popup holds the checkout) re-notifies
 * listeners with the same location object and the last action, which may itself be POP — only a
 * pop that moved counts as leaving.
 */
export function listenForBackNavigation(history: History, onBack: () => void): UnregisterCallback {
  let last = history.location;
  return history.listen((location, action) => {
    const moved = location !== last;
    last = location;
    if (action === 'POP' && moved) onBack();
  });
}

const showPaymentPopupBlockedToast = () =>
  toast(() => <Snackbar label={_t('features.bookingWidget.WidgetPayment.snackbar.blocked')} type="danger" />);

/**
 * Framed Bokadirekt Pay pays in a popup the frame opens inside the click, before the
 * checkout request — `preOpen` waits on nothing here, and nothing may be awaited
 * before it. A saved card is charged by the checkout call itself and pay-at-place
 * pays nothing, so neither opens one; nor does the unframed marketplace. Whatever
 * the submit does, the waiting popup is closed once it has settled without a
 * handover, so no error path has to know about it. Resolves what the call site's
 * `if (!success)` close of its modal expects.
 */
export async function submitAdyenThroughWidgetPopup({
  widgetPayment,
  isBokadirektPay,
  paymentMethodType,
  submitAdyen,
}: {
  widgetPayment: Pick<WidgetPaymentContextValue, 'preOpen' | 'closeIfNotHandedOver'>;
  isBokadirektPay: boolean;
  paymentMethodType: number;
  submitAdyen: () => Promise<boolean>;
}): Promise<boolean> {
  const paysInPopup =
    getEmbedContext() === 'framed' && isBokadirektPay && isEmbedRedirectPaymentMethod(paymentMethodType);
  if (!paysInPopup) return submitAdyen();

  const outcome = widgetPayment.preOpen();
  // A popup is live and was brought forward; its wait screen is in the open modal.
  if (outcome === 'busy') return true;
  // The previous submit has not settled and no popup is live: nothing to wait in.
  if (outcome === 'pending') return false;
  if (outcome === 'blocked') {
    showPaymentPopupBlockedToast();
    return false;
  }

  try {
    return await submitAdyen();
  } catch (error) {
    Sentry.captureException(error);
    return false;
  } finally {
    widgetPayment.closeIfNotHandedOver();
  }
}

export function isWidgetPaymentBlockingSubmit({
  state,
  submitPending,
}: Pick<WidgetPaymentContextValue, 'state' | 'submitPending'>): boolean {
  return submitPending || state !== 'idle';
}

export function showsWidgetPaymentModal({
  embedded,
  state,
}: {
  embedded: boolean;
  state: WidgetPaymentState;
}): boolean {
  return embedded && state !== 'idle';
}

/**
 * Framed: the checkout reacts to the pay popup's outcome. `complete` routes to the
 * confirmation — replaced, not pushed, so Back from a paid booking never reaches
 * its checkout; `error` closes the payment modal, resets the payment and toasts
 * the message the popup or the status poll carried; `aborted` does the same
 * silently, since nothing failed. Each is acknowledged so the next payment in
 * this frame starts idle — `complete` too, as the provider stays mounted on the
 * confirmation route. Unframed, the state never leaves idle.
 */
export function useWidgetPaymentReactions({
  onCheckoutModalClose,
  onResetPayment,
}: {
  onCheckoutModalClose: () => void;
  onResetPayment: () => void;
}) {
  const history = useHistory();
  const { state, pending, result, acknowledge } = useWidgetPayment();

  useEffect(() => {
    if (state === 'complete') {
      if (pending) {
        history.replace({
          pathname: '/booking/confirmation',
          search: `?bookingId=${encodeURIComponent(pending.resourceId)}&type=bokadirektPay`,
        });
      }
      acknowledge();
      return;
    }
    if (state !== 'error' && state !== 'aborted') return;

    onCheckoutModalClose();
    onResetPayment();
    if (state === 'error') {
      toast(
        ({ closeToast }) => (
          <Snackbar
            label={result?.errorMessage || _t(`${baseTranslationKey}.adyenCheckoutError`)}
            type="danger"
            onClose={closeToast}
          />
        ),
        { autoClose: 5000 },
      );
    }
    acknowledge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}

export function bookingCheckoutReducer(state: BookingCheckoutState, action: BookingCheckoutAction) {
  switch (action.type) {
    case 'UPDATE_SUMMARY': {
      const isOnlinePaymentRequired = action.payload.isOnlinePaymentRequired;
      const selectedPaymentMethod = action.payload.availablePaymentMethods.find(
        (method) => method.type === state.selectedPaymentMethod.type,
      );
      const hasSelectedPayAtPlace = state.selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE;
      const nothingToPay = !action.payload.canPayOnline || selectedPaymentMethod?.final === 0;

      return {
        ...state,
        summary: action.payload,
        /**
         * If pay at place is selected when trying to remove the option we need to set the payment method to none
         */
        ...((isOnlinePaymentRequired &&
          hasSelectedPayAtPlace &&
          !nothingToPay && {
            selectedPaymentMethod: action.payload.availablePaymentMethods.find(
              (method) => method.type === CHECKOUT_PAYMENT_METHOD.NONE,
            ),
          }) as SelectedPaymentMethod),
      };
    }
    case 'SET_PAYMENT_METHOD':
      return {
        ...state,
        selectedPaymentMethod: action.payload,
        missingActions: state.missingActions.filter((a) => a !== 'payment-method'),
      };
    case 'UPDATE_FORM_FIELDS': {
      return {
        ...state,
        formFields: action.payload,
      };
    }
    case 'SET_TRIGGERED_TURNSTILE': {
      return {
        ...state,
        triggeredTurnstile: true,
      };
    }
    case 'SHOW_ADD_PARTICIPANT_MODAL': {
      return {
        ...state,
        submitting: false,
        groupBooking: {
          ...state.groupBooking,
          showModal: action.payload,
        },
      };
    }
    case 'UPDATE_GROUP_BOOKING': {
      const updatedFields = filterEmptyValues(action.payload);

      return {
        ...state,
        groupBooking: {
          ...state.groupBooking,
          ...updatedFields,
        },
        ...(action.payload.hasAddedParticipant
          ? { missingActions: state.missingActions.filter((action) => action !== 'participants') }
          : {}),
      };
    }
    case 'SUCCESS_REDIRECT': {
      if (action.payload !== CHECKOUT_PAYMENT_METHOD.SWISH || !(isIPhone() || isAndroidPhone())) {
        removeBookingCheckoutStateFromLocalStorage();
      }

      return {
        ...state,
        submitting: false,
      };
    }
    case 'SUBMITTING': {
      return {
        ...state,
        submitting: true,
      };
    }
    case 'ERROR': {
      return {
        ...state,
        submitting: false,
        error: action.payload,
        cofThreeDS: null,
      };
    }
    case 'ONHOLD_COF': {
      const updatedState = {
        ...state,
        cofThreeDS: action.payload.cofThreeDS,
      };
      localStorage.setItem(
        getCheckoutStateStorageKey(),
        JSON.stringify({ savedCheckoutState: updatedState, ...action.payload }),
      );
      return {
        ...updatedState,
        submitting: false,
      };
    }
    case 'ONHOLD_SWISH': {
      const { bookingRequestBody, bookingTrackingProps, ...swishState } = action.payload;

      localStorage.setItem(
        getCheckoutStateStorageKey(),
        JSON.stringify({
          savedCheckoutState: state,
          bookingRequestBody,
          bookingTrackingProps,
          payment: swishState.payment,
        }),
      );

      return {
        ...state,
        submitting: false,
        swish: swishState,
      };
    }
    case 'ERROR_SWISH': {
      return {
        ...state,
        submitting: false,
        swish: null,
      };
    }
    // Reset any in-flight payment UI, regardless of method. Used when a payment
    // that ran out-of-frame (embedded widget pay popup) fails and the iframe
    // checkout needs to return to a clean, retryable state.
    case 'RESET_PAYMENT': {
      // The snapshot persisted before escaping to the pay popup (see submitAdyen)
      // is no longer needed once the payment aborts/errors — reconciliation won't
      // run. Drop it so PII (email/phone) doesn't linger and a later redirect
      // validation can't replay stale state.
      removeBookingCheckoutStateFromLocalStorage();
      return {
        ...state,
        submitting: false,
        swish: null,
        cofThreeDS: null,
        error: false,
        session: null,
        bookingId: null,
      };
    }
    case 'CLOSE_SWISH_QRCODE_MODAL': {
      return {
        ...state,
        submitting: false,
        swish: { ...state.swish, showQrCode: false },
      };
    }
    case 'SET_PROTECTED_EMAIL_ERROR': {
      return {
        ...state,
        error: false,
        submitting: false,
        protectedEmailError: action.payload,
      };
    }
    case 'UPDATE_TERMS_ACCEPTED': {
      return {
        ...state,
        termsAccepted: action.payload,
        missingActions: state.missingActions.filter((a) => a !== 'practitioner-terms'),
      };
    }
    case 'SET_MISSING_ACTION': {
      const missingActions = Array.from(new Set([...state.missingActions, action.payload]));
      return {
        ...state,
        missingActions,
      };
    }
    case 'SET_GUEST_ID': {
      return {
        ...state,
        guestId: uuidv4(),
      };
    }
    case 'SET_ONLINE_PAYMENT_REQUIRED_EMAIL': {
      return {
        ...state,
        onlinePaymentRequiredEmail: action.payload,
      };
    }
    case 'UPDATE_SESSION': {
      const { bookingId, isRedirectFlow, ...rest } = action.payload;
      return {
        ...state,
        session: rest,
        bookingId: bookingId,
        submitting: false,
      };
    }
    case 'REMOVE_SESSION': {
      return {
        ...state,
        session: null,
        bookingId: null,
        submitting: false,
      };
    }

    default:
      const never: never = action;
      throw new Error(`Unhandled action type: ${never}`);
  }
}

async function handleSubmitPayAtPlace(
  context: SubmitPayAtPlaceBookingContext,
  dispatch: Dispatch<BookingCheckoutAction>,
) {
  const { bookingRequestBody, bookingTrackingProps, fullyPaid, turnstileToken } = context;
  const paymentMethod = CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE;
  const request = { ...bookingRequestBody, paymentMethod, fullyPaid, turnstileToken };

  dispatch({ type: 'SUBMITTING', payload: true });

  const { data, error } = await promiseWrapper(bookServices.saveBooking(request));

  const validateResponse = saveBookingResponseSchema.safeParse(data);

  if (!validateResponse.success || error) {
    context.errorCallback(error, CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE);
    return;
  }

  saveBookingTrackingProps(bookingTrackingProps);
  context.successCallback({ paymentMethod, responseData: validateResponse.data });
}

function buildBookStateRedirectPayload(bookState: BookState) {
  const { services, time, employee, campaigns, place } = bookState;
  const { campaignsIds, dealId } = (campaigns ?? []).reduce<{ campaignsIds: number[]; dealId?: number }>(
    (acc, c) => {
      if (typeof c.id !== 'number') return acc;
      acc.campaignsIds.push(c.id);
      if (c.type === 7 && c.publicationType === 2) acc.dealId = c.id;
      return acc;
    },
    { campaignsIds: [] },
  );
  return {
    employee,
    services: services.map((s) => s.id),
    time: { ...time, employees: (time.employees ?? []).map((e: { id: number }) => e.id) },
    campaignsIds,
    dealId,
    // omit when false to keep URL short — JSON.stringify keeps `false` but drops `undefined`
    isSistaminuten: hasSistaminutenCampaign(campaigns ?? []) || undefined,
    place: { id: place.id, slug: place.about.slug },
  };
}

/**
 * Where Adyen sends the customer back after a redirect leg (3DS, Swish, wallets).
 * The book state rides along base64-encoded so the validate page can rebuild the
 * cart in the fresh document the redirect lands in.
 *
 * In the booking-widget SPA every leg returns to that SPA's own validate route,
 * so a Linked Session keeps its theme and session instead of being dropped into
 * the marketplace SPA at the moment of payment. The theme params ride along too:
 * an app switch (Swish, BankID) can land the return in a fresh tab that holds
 * none of the widget's memos, and the place comes from `data` there.
 */
export function buildBokadirektPayReturnUrl(bookState?: BookState | null): string {
  const validateUrl = new URL(`${withEmbedBasename(url.getBaseUrl())}validate-bokadirekt-pay/booking`);
  if (bookState?.services && bookState?.time && bookState?.place) {
    const bookStateBase64String = Buffer.from(JSON.stringify(buildBookStateRedirectPayload(bookState)))
      .toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    validateUrl.searchParams.append('data', bookStateBase64String);
  }
  if (isEmbedBundle()) {
    Object.entries(getEmbedThemeParams()).forEach(([param, value]) => validateUrl.searchParams.append(param, value));
  }
  return validateUrl.toString();
}

export async function handleSubmitAdyen(
  context: SubmitBookingContext & {
    selectedPaymentMethod: SelectedPaymentMethod;
    walleyPhoneNumber?: string;
    hasUser?: boolean;
    history: History;
    widgetPayment: Pick<WidgetPaymentContextValue, 'handover'>;
  },
  dispatch: Dispatch<BookingCheckoutAction>,
): Promise<boolean> {
  const { bookingRequestBody, bookingTrackingProps, selectedPaymentMethod, bookState, walleyPhoneNumber, hasUser } =
    context;
  const storedCardId =
    selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.STORED_COF ? selectedPaymentMethod.id : undefined;

  if (selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.NONE) {
    const error = new Error('handleSubmitAdyen called with payment method NONE');
    console.error(error);
    Sentry.captureException(error);
    toast(({ closeToast }) => <Snackbar label={_t('serverError')} type="danger" onClose={closeToast} />, {
      autoClose: 5000,
    });
    return false;
  }

  const selectedPaymentType =
    selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.NEW_COF
      ? CHECKOUT_PAYMENT_METHOD.STORED_COF
      : selectedPaymentMethod.type;

  dispatch({ type: 'SUBMITTING', payload: true });

  const bokadirektPayReturnUrl = buildBokadirektPayReturnUrl(bookState);

  const { data, error } = await promiseWrapper(
    bookingsServices.checkout({
      ...bookingRequestBody,
      paymentMethod: selectedPaymentType,
      ...(storedCardId
        ? {
            storedCardId: storedCardId,
            browserInfo: buildBrowserInfo(),
          }
        : {}),
      returnUrl: bokadirektPayReturnUrl,
      walleyPhoneNumber,
    }),
  );

  if (error) {
    dispatch({ type: 'REMOVE_SESSION' });
    context.errorCallback(error, selectedPaymentType);
    return false;
  }

  const resultCodeResponse = bookingCheckoutResultCodeResponseSchema.safeParse(data);
  if (resultCodeResponse.success) {
    if (resultCodeResponse.data.resultCode === 'Authorised') {
      saveBookingTrackingProps(bookingTrackingProps);
      // Embed: nothing left to authenticate, so the validate route reconciles in the
      // frame. Through the router, never navigate(): that would move the merchant page.
      // Replaced, not pushed, so Back never lands on the checkout for a charged booking.
      if (getEmbedContext() === 'framed') {
        context.history.replace(
          buildBokadirektPayReconcilePath({
            resourceType: 'booking',
            resourceId: resultCodeResponse.data.bookingId,
            checkoutRef: resultCodeResponse.data.checkoutRef,
          }),
        );
        return true;
      }
      navigate(
        buildBokadirektPayReconcileUrl({
          resourceType: 'booking',
          resourceId: resultCodeResponse.data.bookingId,
          checkoutRef: resultCodeResponse.data.checkoutRef,
        }),
      );
      return true;
    }
    context.errorCallback(
      {
        message: resultCodeResponse.data.refusalReason ?? 'Payment refused',
        clientError: _t('onlinePaymentOrderRefused.invalid'),
      },
      selectedPaymentType,
    );
    return false;
  }

  const sessionResponse = bookingCheckoutSessionResponseSchema.safeParse(data);
  if (sessionResponse.success) {
    if (getEmbedContext() === 'framed' && isEmbedRedirectPaymentMethod(selectedPaymentMethod.type)) {
      // The frame pre-opened the pay popup in the click; it gets the pay URL now.
      context.widgetPayment.handover({
        sessionId: sessionResponse.data.sessionId,
        checkoutRef: sessionResponse.data.checkoutRef,
        resourceId: String(sessionResponse.data.bookingId),
        resourceType: 'booking',
        paymentMethodType: selectedPaymentMethod.type,
        amount: selectedPaymentMethod.final,
        hasUser,
      });
      return true;
    }
    dispatch({
      type: 'UPDATE_SESSION',
      payload: {
        id: sessionResponse.data.sessionId,
        data: sessionResponse.data.sessionData,
        bookingId: String(sessionResponse.data.bookingId),
        checkoutRef: sessionResponse.data.checkoutRef,
        isRedirectFlow: CHECKOUT_PAYMENT_METHOD.KLARNA === selectedPaymentMethod.type,
      },
    });
    saveBookingTrackingProps(bookingTrackingProps);
    return true;
  }

  context.errorCallback(
    { message: 'Unexpected checkout response', clientError: _t('onlinePaymentOrderRefused.invalid') },
    selectedPaymentType,
  );
  return false;
}

export async function handleSubmitSwish(context: SubmitSwishBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  const { bookingRequestBody, bookingTrackingProps, bookState, swish } = context;
  const paymentMethod = CHECKOUT_PAYMENT_METHOD.SWISH;
  const bookStateBase64String = Buffer.from(JSON.stringify(buildBookStateRedirectPayload(bookState))).toString(
    'base64',
  );

  dispatch({ type: 'SUBMITTING', payload: true });

  const bookingRequestData = {
    ...bookingRequestBody,
    paymentMethod,
    ...(isIPhone() || isAndroidPhone()
      ? (() => {
          const search = new URLSearchParams({
            data: bookStateBase64String,
          });
          const validateUrl = new URL(`${url.getBaseUrl()}booking/checkout/validate-swish-redirect`);
          search.forEach((value, key) => {
            validateUrl.searchParams.append(key, value);
          });
          return {
            callbackUrl: validateUrl.toString(),
          };
        })()
      : {}),
  };

  const shouldUpdateExistingBooking = Boolean(swish?.payment?.bookingId);

  let createOrderResponse = null;
  let createOrUpdateOrderError = null;

  if (shouldUpdateExistingBooking) {
    const { error: updateError } = await promiseWrapper(
      bookServices.updateSwishOrder({ ...bookingRequestData, bookingId: swish.payment.bookingId }),
    );
    createOrUpdateOrderError = updateError;
  } else {
    const { data, error: createOrderError } = await promiseWrapper(bookServices.createSwishOrder(bookingRequestData));
    createOrUpdateOrderError = createOrderError;
    createOrderResponse = data;
  }

  const validateResponse = swishPaymentBookingResponseSchema.safeParse(
    shouldUpdateExistingBooking ? swish.payment : createOrderResponse,
  );

  if (!validateResponse.success || createOrUpdateOrderError) {
    context.errorCallback(createOrUpdateOrderError, CHECKOUT_PAYMENT_METHOD.SWISH);
    return;
  }

  const { bookingId, qrCode } = validateResponse.data;
  const successUrl = `${url.getBaseUrl()}booking/confirmation?bookingId=${bookingId}&type=swish`;

  if (!qrCode && !isIPhone() && !isAndroidPhone()) {
    context.errorCallback(
      {
        clientError: _s(`${baseTranslationKey}.swishModal.snackbar.bookingMessage`),
        message: 'Qr-code is missing',
        status: 500,
      },
      CHECKOUT_PAYMENT_METHOD.SWISH,
    );
    return;
  }

  const alreadyListeningOnSwishPayment = Boolean(swish?.notificationChannel);
  const eventSource =
    swish?.notificationChannel ?? swishServices.waitForSwishNotification(validateResponse.data.channel);

  dispatch({
    type: 'ONHOLD_SWISH',
    payload: {
      payment: validateResponse.data,
      notificationChannel: eventSource,
      showQrCode: !isIPhone() && !isAndroidPhone(),
      bookingRequestBody: context.bookingRequestBody,
      bookingTrackingProps: context.bookingTrackingProps,
    },
  });

  if (isIPhone() || isAndroidPhone()) {
    navigate(validateResponse.data.url);
  }

  if (eventSource && !alreadyListeningOnSwishPayment) {
    eventSource.onmessage = (e) => {
      const swishNotification = JSON.parse(e.data);

      if (swishNotification.success) {
        saveBookingTrackingProps(bookingTrackingProps);
        context.successCallback({ paymentMethod, responseData: { ...validateResponse.data, url: successUrl } });
        eventSource.close();
      } else {
        if (swishNotification.keepAlive) {
          return;
        }

        eventSource.close();
        dispatch({ type: 'ERROR_SWISH' });

        // This means that the swish payment timed out, user most likely
        // switched payment method or have not scanned qrcode
        if (swishNotification.errorReason === 'TM01') {
          return;
        }
        toast(
          ({ closeToast }) => (
            <Snackbar
              type="danger"
              label={
                swishNotification.clientError
                  ? swishNotification.clientError
                  : _s(`${baseTranslationKey}.swishModal.snackbar.bookingMessage`)
              }
              action={
                <button
                  onClick={() => {
                    closeToast();
                  }}>
                  {_s(`${baseTranslationKey}.swishModal.snackbar.cta`)}
                </button>
              }
            />
          ),
          {
            autoClose: 3000,
          },
        );
      }
    };

    eventSource.onerror = () => {
      dispatch({ type: 'ERROR_SWISH' });
      context.errorCallback(
        { message: 'Could not subscribe to swish notification events', status: 500 },
        CHECKOUT_PAYMENT_METHOD.SWISH,
      );
    };
  }
}

function handleSubmitGuestCoF(context: SubmitCofBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  return async (paymentData: CofPaymentData) => {
    const { bookingRequestBody, bookingTrackingProps } = context;
    const paymentMethod = (() => {
      if (paymentData?.paymentMethod?.type === 'googlepay') return CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY;
      if (paymentData?.paymentMethod?.type === 'applepay') return CHECKOUT_PAYMENT_METHOD.APPLE_PAY;
      return CHECKOUT_PAYMENT_METHOD.STORED_COF;
    })();
    const request = { ...bookingRequestBody, paymentMethod, paymentData };

    dispatch?.({ type: 'SUBMITTING', payload: true });

    const { data, error } = await promiseWrapper(bookServices.cofPayment(request));

    const successData = cofPaymentResponseSuccessSchema.safeParse(data);

    if (successData.success) {
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: { step: 'success', booking_session_id: bookingFlowTrackingSession.getId() },
      });
      saveBookingTrackingProps(bookingTrackingProps);
      context.successCallback({ paymentMethod, responseData: successData.data });
      return;
    }
    const threeDSData = cofPaymentResponseThreeDSSchema.safeParse(data);
    if (threeDSData.success) {
      dispatch?.({
        type: 'ONHOLD_COF',
        payload: {
          cofThreeDS: threeDSData.data,
          bookingRequestBody: context.bookingRequestBody,
          bookingTrackingProps: context.bookingTrackingProps,
        },
      });
      return;
    }

    trackMpEvent({
      name: 'add_payment_card_flow',
      properties: { step: 'error', booking_session_id: bookingFlowTrackingSession.getId() },
    });

    context.errorCallback(error, paymentMethod);
  };
}

export function handleSubmitCoF(context: SubmitCofBookingContext, dispatch?: Dispatch<BookingCheckoutAction>) {
  return async (storedPaymentMethodId: string) => {
    const { bookingRequestBody, bookingTrackingProps } = context;
    const paymentMethod = CHECKOUT_PAYMENT_METHOD.STORED_COF;
    const request: CofPaymentRequest = {
      ...bookingRequestBody,
      paymentMethod,
      paymentData: {
        paymentMethod: {
          type: 'storedCard',
          storedPaymentMethodId,
        },
      },
    };

    dispatch?.({ type: 'SUBMITTING', payload: true });

    const { data, error } = await promiseWrapper(bookServices.cofPayment(request));

    const successData = cofPaymentResponseSuccessSchema.safeParse(data);

    if (successData.success) {
      saveBookingTrackingProps(bookingTrackingProps);
      context.successCallback({ paymentMethod, responseData: successData.data });
      return;
    }
    const threeDSData = cofPaymentResponseThreeDSSchema.safeParse(data);
    if (threeDSData.success) {
      dispatch?.({
        type: 'ONHOLD_COF',
        payload: {
          cofThreeDS: threeDSData.data,
          bookingRequestBody: context.bookingRequestBody,
          bookingTrackingProps: context.bookingTrackingProps,
        },
      });
      return;
    }

    context.errorCallback(error, paymentMethod);
  };
}

function handleSubmitGooglePay(context: SubmitGooglePayBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  return async (final: number) => {
    try {
      const googlePaymentsClient = clientInstance('ecom');
      const isReadyToPay = googlePaymentsClient.isReadyToPay(getGoogleIsReadyToPayRequest('ecom'));

      if (!isReadyToPay) throw new Error('Google pay is not ready to pay');

      const { services, products, giftcards, fees } = context;

      dispatch({ type: 'SUBMITTING', payload: true });

      const response = await googlePaymentsClient.loadPaymentData(
        getGooglePayPaymentDataRequest(
          {
            totalPrice: `${final.toFixed(2)}`,
            totalPriceStatus: 'FINAL',
            totalPriceLabel: _s('googlepay.totalPriceLabel.paynow'),
            currencyCode: 'SEK',
            countryCode: 'SE',
            displayItems: [...services, ...products, ...giftcards, ...fees],
          },
          'ecom',
        ),
      );

      await completeWalletBooking(
        {
          ...context,
          paymentData: {
            paymentMethod: {
              type: 'googlepay',
              googlePayToken: response.paymentMethodData.tokenizationData.token,
              googlePayCardNetwork: response.paymentMethodData.info.cardNetwork,
            },
            browserInfo: buildBrowserInfo(),
            redirectInfo: {
              returnPath: '/booking/checkout/validate-3ds-redirect',
            },
          },
          type: 'googlepay',
        },
        dispatch,
      );
    } catch (error) {
      // Google pay throws "CANCELED statusCode when user closes the payments sheet"
      const isUserCancelled =
        error?.statusCode === 'CANCELED' || error?.status === 'CANCELED' || error?.name === 'AbortError';

      if (isUserCancelled) {
        context.abortCallback(CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY);
      } else {
        context.errorCallback(error, CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY);
      }
      dispatch({ type: 'ERROR', payload: true });
    }
  };
}

function handleSubmitApplePay(context: SubmitApplePayBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  return async (final: number) => {
    const { services, products, giftcards, fees } = context;

    const applePaySession = createSession(
      {
        total: mapApplePayLineItem({ amount: `${final}`, label: 'Bokadirekt' }),
        lineItems: [...services, ...products, ...giftcards, ...fees],
      },
      'ecom',
    );

    applePaySession.begin();

    applePaySession.oncancel = () => context.abortCallback(CHECKOUT_PAYMENT_METHOD.APPLE_PAY);

    applePaySession.onpaymentauthorized = async (payment) => {
      try {
        await completeWalletBooking(
          {
            ...context,
            paymentData: {
              paymentMethod: {
                type: 'applepay',
                applePayToken: payment.payment.token.paymentData,
              },
              redirectInfo: {
                returnPath: '/booking/checkout/validate-3ds-redirect',
              },
            },
            type: 'applepay',
          },
          dispatch,
          applePaySession,
        );
      } catch (error) {
        context.errorCallback(error, CHECKOUT_PAYMENT_METHOD.APPLE_PAY);

        applePaySession.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [],
        });
        dispatch({ type: 'ERROR', payload: true });
      }
    };
  };
}

async function completeWalletBooking(
  context: CompleteWalletBookingContext,
  dispatch: Dispatch<BookingCheckoutAction>,
  applePaySession?: ApplePaySession,
) {
  const { bookingRequestBody, type, paymentData, bookingTrackingProps } = context;
  const paymentMethod = type === 'googlepay' ? CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY : CHECKOUT_PAYMENT_METHOD.APPLE_PAY;

  let recurringDetailReference: string;

  const request: CofPaymentRequest = {
    ...bookingRequestBody,
    paymentMethod,
    ...(() => {
      if (recurringDetailReference) {
        return {
          paymentData: {
            paymentMethod: {
              type: 'storedCard',
              storedPaymentMethodId: recurringDetailReference,
            },
          },
        };
      } else {
        return { paymentData };
      }
    })(),
  };

  const { data, error } = await promiseWrapper(bookServices.cofPayment(request));

  const successData = cofPaymentResponseSuccessSchema.safeParse(data);

  if (successData.success) {
    applePaySession?.completePayment?.({ status: ApplePaySession.STATUS_SUCCESS });
    saveBookingTrackingProps(bookingTrackingProps);
    context.successCallback({ paymentMethod, responseData: successData.data });
    return;
  }

  const threeDSData = cofPaymentResponseThreeDSSchema.safeParse(data);

  if (threeDSData.success) {
    dispatch({
      type: 'ONHOLD_COF',
      payload: {
        cofThreeDS: threeDSData.data,
        bookingRequestBody: context.bookingRequestBody,
        bookingTrackingProps: context.bookingTrackingProps,
      },
    });
    return;
  }

  applePaySession?.completePayment?.({ status: ApplePaySession.STATUS_FAILURE });
  context.errorCallback(error, paymentMethod);
}

export function handleSubmitThreeDSPayment(context: SubmitThreeDSContext, dispatch?: Dispatch<BookingCheckoutAction>) {
  return async (_state: any) => {
    const { bookingId, reference } = context.cofThreeDS || {};

    dispatch?.({ type: 'SUBMITTING', payload: true });

    const { data, error } = await promiseWrapper(
      adyenServices.submitAdditionalDetails({
        bookingId,
        details: _state.data.details,
        reference,
      }),
    );

    const validateResponse = submitAdditionalDetailsResponseSchema.safeParse(data);

    if (!validateResponse.success || error) {
      context.errorCallback(error, context.paymentMethod);
      return;
    }

    saveBookingTrackingProps(context.bookingTrackingProps);

    context.successCallback({
      paymentMethod: CHECKOUT_PAYMENT_METHOD.STORED_COF,
      responseData: validateResponse.data,
    });
  };
}

async function handleInitKlarnaPayment(context: InitKlarnaBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  const { bookingRequestBody, bookingTrackingProps } = context;
  const paymentMethod = CHECKOUT_PAYMENT_METHOD.KLARNA;

  dispatch({ type: 'SUBMITTING', payload: true });
  const { data, error } = await promiseWrapper(bookServices.initKlarna({ ...bookingRequestBody, paymentMethod }));

  const validateResponse = initKlarnaResponseSchema.safeParse(data);

  if (!validateResponse.success || error) {
    context.errorCallback(error, paymentMethod);
    return;
  }

  saveBookingTrackingProps(bookingTrackingProps);

  context.successCallback({ paymentMethod, responseData: validateResponse.data });
}

async function handleInitQliroPayment(context: InitQliroBookingContext, dispatch: Dispatch<BookingCheckoutAction>) {
  const { bookingRequestBody, bookingTrackingProps } = context;
  const paymentMethod = CHECKOUT_PAYMENT_METHOD.QLIRO;

  dispatch({ type: 'SUBMITTING', payload: true });

  const { data, error } = await promiseWrapper(bookServices.initQliro({ ...bookingRequestBody, paymentMethod }));

  const validateResponse = initQliroResponseSchema.safeParse(data);

  if (!validateResponse.success || error) {
    context.errorCallback(error, paymentMethod);
    return;
  }

  saveBookingTrackingProps(bookingTrackingProps);

  context.successCallback({ paymentMethod, responseData: validateResponse.data });
}

/**
 * Currently if user has some fields prefilled in his profile we remove them from form fields
 * since we instead want to show them in EditUserInfo modal component.
 * (this logic is handled by giving "state.formData" to the "UpdateUserInfo" Component)
 *
 * If user does not have these fields prefilled they will instead not be filtered from form fields
 * and be shown in the form.
 */
function handleRemovePrefilledUserProfileFields(
  userProfileInfoFields: { id: FormField['id']; value: string }[],
  formFields: FormField[],
  dispatch: Dispatch<BookingCheckoutAction>,
) {
  const filterExistingProfileFields = formFields.filter(
    (field) => !userProfileInfoFields.find((v) => v.id === field.id),
  );

  dispatch({ type: 'UPDATE_FORM_FIELDS', payload: filterExistingProfileFields });
}

const useBookingCheckoutManager = (
  booking: BookState,
  cards: PaymentCard[],
  formData: CheckoutFormData,
  paymentHistory: PaymentHistoryResponse,
): UseBookingCheckoutManagerResult => {
  const user = useAppSelector((state) => state.users.user);
  const appDispatch = useDispatch();
  const location = useLocation<{ savedCheckoutState?: unknown }>();
  const history = useHistory();
  const widgetPayment = useWidgetPayment();
  const { paymentMethods, platformFee, isBokadirektPay } = usePaymentMethodsContext();
  const validSavedCheckoutState = bookingCheckoutStateSchema.safeParse(location.state?.savedCheckoutState);
  const selectedCapacity = booking.time?.selectedCapacity;
  const isSistaminuten = isSistaminutenInstance() || hasSistaminutenCampaign(booking.campaigns);
  const isDeal = hasDealCampaign(booking.campaigns);
  const { legacyPaymentsUnavailable } = getContainmentPolicy();
  const hasFetchedUser = useAppSelector((state) => state.users.hasFetched);
  const isSistaMinutenGuestUser = isSistaminuten && !user;

  const getExperimentVariant = useGetAmplitudeExperimentVariant();

  // experiment with radio buttons
  const usePromotedPaymentMethods = (() => {
    if (isSistaminutenInstance()) return false;

    return getExperimentVariant('promoted-payment-methods-web')?.value === 'variant-options-visible';
  })();

  const employee = getBookingCheckoutEmployee(location.state, booking);
  const formFields = useMemo(
    () =>
      getBookingCheckoutFormFields({
        employee: employee.actual,
        place: booking.place,
        services: booking.services,
        /**
         * We force require email if we are on sistaminuten since online payment is required and we need to
         * make sure we have an email to send reciept
         */
        forceRequireEmail: isSistaminuten,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const emailRequired = (() => {
    if (Boolean(user?.contact?.email)) return true;

    return formFields.some((field) => {
      if (field.id !== 'email') return false;
      return field.required;
    });
  })();

  const guestId = (() => {
    if (!user && (isSistaminutenInstance() || guestCheckoutManager().getOptions()?.useGuestCheckout)) {
      return uuidv4();
    }
    return undefined;
  })();

  const isGuestCheckout = Boolean(guestId);

  const summary = getBookingCheckoutSummary({
    booking,
    employee,
    cards,
    emailRequired,
    isGuestCheckout,
    paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
    platformFee: platformFee?.amount,
    forceRequireOnlinePayment: isSistaminuten || isDeal,
    isOnlinePaymentRequiredCustomer: booking.place.isOnlinePaymentRequiredCustomer,
    onlinePaymentMethods: paymentMethods,
    legacyPaymentsUnavailable,
  });
  const { savedCheckoutState, savedSelectedPaymentMethod } = (() => {
    if (!validSavedCheckoutState.success) {
      return { savedCheckoutState: null, savedSelectedPaymentMethod: null };
    }

    const { selectedPaymentMethod, ...savedCheckoutState } = validSavedCheckoutState.data;

    return {
      savedCheckoutState,
      savedSelectedPaymentMethod: selectedPaymentMethod,
    };
  })();

  const preSelectedPaymentMethod = ((): CheckoutSummary['preSelectedPaymentMethod'] => {
    const tryGetSavedSelectedPaymentMethod = (() => {
      if (!savedSelectedPaymentMethod) return undefined;

      const validateIsCoF = selectedStoredCofPaymentMethodSchema.safeParse(savedSelectedPaymentMethod);

      if (validateIsCoF.success) {
        const card = cards.find((card) => card.id === validateIsCoF.data.id);
        if (!card) return undefined;

        const { total, totalLabel, final } = validateIsCoF.data;

        return {
          type: CHECKOUT_PAYMENT_METHOD.STORED_COF,
          brand: card.brand,
          id: card.id,
          lastFour: card.lastFour,
          total,
          final,
          totalLabel,
        };
      } else {
        const validateIsAvailablePreSelectedMethod =
          availablePrePaymentMethodSchema.safeParse(savedSelectedPaymentMethod);

        if (validateIsAvailablePreSelectedMethod.success) {
          return validateIsAvailablePreSelectedMethod.data;
        }
      }

      return undefined;
    })();

    if (tryGetSavedSelectedPaymentMethod) return tryGetSavedSelectedPaymentMethod;

    return summary.preSelectedPaymentMethod;
  })();

  const selectedPaymentMethod =
    preSelectedPaymentMethod ||
    summary.availablePaymentMethods.find((method) => method.type === CHECKOUT_PAYMENT_METHOD.NONE);

  const [state, dispatch] = useReducer(
    bookingCheckoutReducer,
    savedCheckoutState
      ? {
          ...savedCheckoutState,
          selectedPaymentMethod,
          submitting: false,
          cofThreeDS: null,
          error: false,
        }
      : {
          selectedPaymentMethod,
          formData,
          summary,
          formFields,
          submitting: false,
          error: false,
          protectedEmailError: false,
          onlinePaymentRequiredEmail: booking.place.isOnlinePaymentRequiredCustomer,
          cofThreeDS: null,
          session: null,
          termsAccepted: !Boolean(employee.terms),
          missingActions: [],
          swish: null,
          guestId,
          groupBooking: {
            showModal: false,
            isUpdate: false,
            participantId: '1',
            hasAddedParticipant: false,
          },
        },
  );

  useEffect(() => {
    if (!hasFetchedUser) {
      return;
    }

    if (isSistaMinutenGuestUser) {
      handleUseGuestCheckout();
    }
  }, [hasFetchedUser]);

  /**
   * close connection to swish notification channel on unmount
   */
  useEffect(() => {
    return () => state.swish?.notificationChannel?.close?.();
  }, [state.swish?.notificationChannel]);

  useEffect(() => {
    if (!user) return;
    const savedParticipants = getGroupBookingParticipantSession(booking, String(employee.actual.id));
    const userProfileInfoFields = getUserProfileInfoFields(user);

    if (savedParticipants) {
      handleUpdateGroupBooking({ hasAddedParticipant: true });
    }

    handleRemovePrefilledUserProfileFields(userProfileInfoFields, formFields, dispatch);

    /**
     * If isOnlinePaymentRequiredCustomer is undefined user has come from logged out state
     * to checkout. If they login we need to check if they are an online payment required customer
     */
    if (booking.place.isOnlinePaymentRequiredCustomer === undefined) {
      handleOnValidateOnlinePaymentRequiredEmail(booking.place.id, user.contact.email);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    dispatch({ type: 'ERROR_SWISH' });
    if (typeof booking.successGiftCard === 'boolean' && !booking.successGiftCard) return;
    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: getBookingCheckoutSummary({
        booking,
        employee,
        cards,
        selectedCapacity,
        emailRequired,
        isGuestCheckout,
        platformFee: platformFee?.amount,
        paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
        forceRequireOnlinePayment: isSistaminuten || isDeal,
        isOnlinePaymentRequiredCustomer: state.onlinePaymentRequiredEmail,
        onlinePaymentMethods: paymentMethods,
        legacyPaymentsUnavailable,
      }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.usageReqId?.giftcards, selectedCapacity]);

  useEffect(() => {
    /**
     * On initial load and when giftcards or bundle changes we need to update tracking properties
     * for booking flow tracking
     */
    const buildSummaryProperties = getSummaryProperties({
      summary,
      selectedPaymentMethod,
      employee,
      isBokadirektPay,
      hasPlatformFee: isBokadirektPay && (platformFee?.amount ?? 0) > 0,
    });
    bookingFlowTrackingSession.setProperties(
      buildSummaryProperties({ type: 'booking_flow', data: { ...booking, formFields } }),
    );
  }, [booking?.usageReqId?.giftcards, booking.appliedBundle]);

  /**
   * Report the summary view once per mount, in its own effect.
   *
   * `WithPaymentMethodsLoaded` gates this provider on the merchant-settings
   * request, so `platformFee` is resolved by now; it gates on `loading` only, so
   * a failed request still reports the fee as absent.
   */
  useEffect(() => {
    bookingFlowTrackingSession.summaryViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /**
     * We have to set payment method to pay at place if we can't pay online, for example if we
     * apply a giftcard that covers the whole amount
     */
    if (!summary.canPayOnline)
      dispatch({ type: 'SET_PAYMENT_METHOD', payload: { type: CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE } });
  }, [summary.canPayOnline]);

  const handleUpdateGroupBooking = ({
    showModal,
    isUpdate,
    participantId,
    hasAddedParticipant,
  }: {
    showModal?: boolean;
    isUpdate?: boolean;
    participantId?: string;
    hasAddedParticipant?: boolean;
  }) => {
    dispatch({ type: 'UPDATE_GROUP_BOOKING', payload: { isUpdate, participantId, showModal, hasAddedParticipant } });
  };

  const handleChangePaymentMethod = async (method: SelectedPaymentMethod) => {
    if (method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF && user) {
      try {
        const response = await paymentsServices.createTokenizeCardSession();
        dispatch({
          type: 'UPDATE_SESSION',
          payload: { id: response.data.sessionId, data: response.data.sessionData, isCard: true },
        });
        return;
      } catch (error) {
        console.error('Error creating tokenize card session:', error);
      }
    } else {
      dispatch({ type: 'REMOVE_SESSION' });
    }

    dispatch({
      type: 'SET_PAYMENT_METHOD',
      payload:
        method.type === CHECKOUT_PAYMENT_METHOD.NONE
          ? summary.availablePaymentMethods.find((method) => method.type === CHECKOUT_PAYMENT_METHOD.NONE)
          : method,
    });
  };

  const handleApplyGiftcard = (code: string) => (callback: (props) => void) => {
    const { place, usageReqId, services, time } = booking;
    const { finalWithoutGiftcards } = state.summary;

    const giftcardRequestBody: ValidateGiftCardRequest = {
      code,
      place: place.id,
      usageRequestId: usageReqId.uuid,
      personId: employee.selectedEmployeeId,
      startTime: getBookingStartTime(time),
      serviceIds: services?.map((service) => service.id),
      formId: 0,
      amount: finalWithoutGiftcards * 100,
    };

    dispatch({ type: 'ERROR_SWISH' });
    // @ts-ignore
    appDispatch(bookActions.addGiftCard(giftcardRequestBody, callback));
  };

  const handleRemoveGiftcard = (code: string) => () => {
    const { usageReqId } = booking;
    const { summary } = state;

    const giftcardCoveredBookingAmount = !summary.canPayOnline;

    // @ts-ignore
    appDispatch(bookActions.removeGiftCard({ usageRequestId: usageReqId.uuid, code: code }));
    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: getBookingCheckoutSummary({
        booking,
        employee,
        cards,
        selectedCapacity,
        emailRequired,
        platformFee: platformFee?.amount,
        isGuestCheckout,
        paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
        forceRequireOnlinePayment: isSistaminuten || isDeal,
        isOnlinePaymentRequiredCustomer: state.onlinePaymentRequiredEmail,
        onlinePaymentMethods: paymentMethods,
        legacyPaymentsUnavailable,
      }),
    });

    dispatch({ type: 'ERROR_SWISH' });

    /**
     * if the giftcard covered the total amount of the booking and is removed
     * we unselect the checkout payment method
     */
    if (giftcardCoveredBookingAmount) {
      dispatch({ type: 'SET_PAYMENT_METHOD', payload: { type: CHECKOUT_PAYMENT_METHOD.NONE } });
    }
  };

  const handleRemoveBundle = () => {
    appDispatch(bookActions.removeBundle());
    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: getBookingCheckoutSummary({
        booking,
        employee,
        cards,
        selectedCapacity,
        emailRequired,
        isGuestCheckout,
        paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
        forceRequireOnlinePayment: isSistaminuten || isDeal,
        isOnlinePaymentRequiredCustomer: state.onlinePaymentRequiredEmail,
        onlinePaymentMethods: paymentMethods,
        legacyPaymentsUnavailable,
        platformFee: platformFee?.amount,
      }),
    });

    if (!summary.canPayOnline) {
      dispatch({ type: 'SET_PAYMENT_METHOD', payload: { type: CHECKOUT_PAYMENT_METHOD.NONE } });
    }
  };

  const handleDismissSwishQRCode = () => {
    dispatch({ type: 'CLOSE_SWISH_QRCODE_MODAL' });
    bookingFlowTrackingSession.checkoutPaymentMethod('cancel', 'swish');
  };

  const handleToggleTerms = (checked: boolean) => {
    dispatch({ type: 'UPDATE_TERMS_ACCEPTED', payload: checked });
  };

  const handleOnCheckoutMissingAction = (action: CheckoutMissingAction) => {
    dispatch({ type: 'SET_MISSING_ACTION', payload: action });
  };

  const handleUseGuestCheckout = () => {
    guestCheckoutManager().setOptions({ useGuestCheckout: true });

    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: getBookingCheckoutSummary({
        booking,
        employee,
        cards,
        selectedCapacity,
        emailRequired,
        platformFee: platformFee?.amount,
        isGuestCheckout: true,
        paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
        forceRequireOnlinePayment: isSistaminuten || isDeal,
        isOnlinePaymentRequiredCustomer: state.onlinePaymentRequiredEmail,
        onlinePaymentMethods: paymentMethods,
        legacyPaymentsUnavailable,
      }),
    });
    dispatch({ type: 'SET_GUEST_ID' });
  };

  const handleDismissEmailProtectedError = () => {
    dispatch({ type: 'SET_PROTECTED_EMAIL_ERROR', payload: false });
  };

  const handleCloseAdyenCheckoutModal = () => {
    const method = mapSelectedPaymentMethod(state.selectedPaymentMethod.type);
    if (method) bookingFlowTrackingSession.checkoutPaymentMethod('cancel', method);
    dispatch({ type: 'REMOVE_SESSION' });
  };

  const handleOnValidateEmailProtected = async (email: string, onErrorCallback: () => void) => {
    const response = await promiseWrapper(userService.isEmailProtected(email));

    if (response?.error?.status === 401) {
      dispatch({ type: 'SET_PROTECTED_EMAIL_ERROR', payload: true });
      onErrorCallback();
      return;
    }
  };

  const handleOnValidateOnlinePaymentRequiredEmail = async (placeId: number, email: string) => {
    const response = await promiseWrapper(placeService.isOnlinePaymentRequired(placeId, email));
    const isOnlinePaymentRequiredCustomer = response?.error?.status === 403;

    if (state.onlinePaymentRequiredEmail === isOnlinePaymentRequiredCustomer) return;

    dispatch({ type: 'SET_ONLINE_PAYMENT_REQUIRED_EMAIL', payload: isOnlinePaymentRequiredCustomer });
    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: getBookingCheckoutSummary({
        booking,
        employee,
        cards,
        selectedCapacity,
        emailRequired,
        isGuestCheckout,
        paymentHistory: usePromotedPaymentMethods ? paymentHistory : undefined,
        forceRequireOnlinePayment: isSistaminuten || isDeal,
        isOnlinePaymentRequiredCustomer,
        onlinePaymentMethods: paymentMethods,
        legacyPaymentsUnavailable,
        platformFee: platformFee?.amount,
      }),
    });
  };

  const handleSuccessRedirect = ({ paymentMethod, responseData }: SuccessRedirectData) => {
    dispatch({ type: 'SUCCESS_REDIRECT', payload: paymentMethod });
    switch (paymentMethod) {
      case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE: {
        history.push({ pathname: `/booking/confirmation`, search: `?b=${responseData.bookingCode}`, state: {} });
        break;
      }
      case CHECKOUT_PAYMENT_METHOD.SWISH: {
        navigate(responseData.url);
        break;
      }
      case CHECKOUT_PAYMENT_METHOD.KLARNA: {
        history.push({
          pathname: '/booking/checkout/klarna',
          state: { klarnaOrder: responseData, savedCheckoutState: { ...state, formData }, employee: employee.actual },
        });
        break;
      }
      case CHECKOUT_PAYMENT_METHOD.QLIRO: {
        history.push({
          pathname: '/booking/checkout/qliro',
          state: { qliroOrder: responseData, savedCheckoutState: { ...state, formData }, employee: employee.actual },
        });
        break;
      }
      case CHECKOUT_PAYMENT_METHOD.STORED_COF:
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY: {
        const selectedPaymentMethod = (() => {
          if (state.selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.STORED_COF) {
            return {
              type: state.selectedPaymentMethod.type,
              id: state.selectedPaymentMethod.id,
              brand: state.selectedPaymentMethod.brand,
              lastFour: state.selectedPaymentMethod.lastFour,
            };
          }

          if (state.selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY) {
            return { type: CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY };
          }

          if (state.selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.APPLE_PAY) {
            return { type: CHECKOUT_PAYMENT_METHOD.APPLE_PAY };
          }

          return undefined;
        })();

        history.push({
          pathname: `/booking/confirmation`,
          search: `?bookingId=${responseData.id}&type=cof`,
          state: { ...(selectedPaymentMethod && { selectedPaymentMethod }) },
        });
        break;
      }
      default:
        Sentry.captureMessage('User did not get redirected correctly');
        break;
    }
  };

  const handleError = (error: unknown, paymentMethod: CheckoutPaymentMethod) => {
    const errorData = errorResponseSchema.safeParse(error);
    dispatch({ type: 'ERROR', payload: true });

    switch (paymentMethod) {
      /**
       * Qliro and Klarna tracks errors differently since
       * the flow is different from other payment methods where they
       * are not initiated until after booking reservation is created
       */
      case CHECKOUT_PAYMENT_METHOD.KLARNA:
        bookingFlowTrackingSession.error(['klarna_error']);
        break;
      case CHECKOUT_PAYMENT_METHOD.QLIRO:
        bookingFlowTrackingSession.error(['qliro_error']);
        break;
      case CHECKOUT_PAYMENT_METHOD.SWISH:
        bookingFlowTrackingSession.checkoutPaymentMethod('error', 'swish');
        break;
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
        bookingFlowTrackingSession.checkoutPaymentMethod('error', 'google_pay');
        break;
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
        bookingFlowTrackingSession.checkoutPaymentMethod('error', 'apple_pay');
        break;
    }

    const displayClientError = (errorMessage?: string) => {
      toast(
        ({ closeToast }) => (
          <Snackbar
            label={errorMessage ?? _s('serverError')}
            type="danger"
            onClose={() => {
              dispatch({ type: 'ERROR', payload: false });
              closeToast();
            }}
          />
        ),
        {
          autoClose: 5000,
        },
      );
    };

    if (!errorData.success) {
      displayClientError();
      Sentry.captureException(error);
      return;
    }

    const { status, clientError } = errorData.data;
    /**
     * if we get 409 conflict error the booking time is no longer available and we need to redirect the user to
     * calendar page where the location state of retry is set to true to show a Alert message saying the time is taken
     */
    if (status === 409) {
      history.push({ pathname: getBookingRetryPathname(booking), state: { retry: true } });
      return;
    }

    if (status === 401 && !isSistaminutenInstance()) {
      /**
       * if we get 401 unauthorized error the user has tried to book with a protected email
       * save checkout state if user proceeds to login to save the current state and complete the booking
       * since user login otherwise will loose the current state
       */
      history.replace('/booking/checkout', { ...location?.state, savedCheckoutState: state });
      dispatch({ type: 'SET_PROTECTED_EMAIL_ERROR', payload: true });
      return;
    }

    displayClientError(clientError);
    Sentry.captureException(error);
  };

  const bookingCheckoutAPI = (): CheckoutAPI => {
    const bookingRequestBody = getBookingRequestBody({
      booking,
      employee,
      formfields: formData,
      guestId: state.guestId,
      summary,
    });

    /**
     * Handle tracking and success redirects
     */
    const successCallback = (props: SuccessRedirectData) => {
      switch (props.paymentMethod) {
        case CHECKOUT_PAYMENT_METHOD.KLARNA:
          bookingFlowTrackingSession.checkoutPaymentMethod('initiated', 'klarna');
          break;
        case CHECKOUT_PAYMENT_METHOD.QLIRO:
          bookingFlowTrackingSession.checkoutPaymentMethod('initiated', 'qliro');
          break;
        case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
          bookingFlowTrackingSession.checkoutPaymentMethod('success', 'google_pay');
          break;
        case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
          bookingFlowTrackingSession.checkoutPaymentMethod('success', 'apple_pay');
          break;
        case CHECKOUT_PAYMENT_METHOD.SWISH:
          /**
           * Since swish redirects directly we have to flush the tracking event immediately
           * for it to be sent before the redirect happens
           */
          bookingFlowTrackingSession.checkoutPaymentMethod('success', 'swish', { flush: true });
          break;
      }

      handleSuccessRedirect(props);
    };

    const errorCallback = handleError;

    const abortCallback = (paymentMethod: CheckoutPaymentMethod) => {
      switch (paymentMethod) {
        case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
          bookingFlowTrackingSession.checkoutPaymentMethod('cancel', 'google_pay');
          break;
        case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
          bookingFlowTrackingSession.checkoutPaymentMethod('cancel', 'apple_pay');
          break;
      }
    };

    const bookingTrackingProps = buildBookingTrackingProps({
      booking,
      employee,
      summary,
      selectedPaymentMethod: state.selectedPaymentMethod,
      formFields,
      selectedCapacity,
    });

    const applePaySummary = {
      services: getApplePaySummaryServiceItems(mapBookStateToCheckoutDetailsServices(booking, employee.shown)),
      products: getApplePaySummaryProductItems(mapBookStateToCheckoutDetailsProducts(booking)),
      giftcards: getApplePaySummaryGiftcardItems(booking),
      fees: getApplePaySummaryFeeItems(state.selectedPaymentMethod),
    };

    const googlePaySummary = {
      services: getGooglePaySummaryServiceItems(mapBookStateToCheckoutDetailsServices(booking, employee.shown)),
      products: getGooglePaySummaryProductItems(mapBookStateToCheckoutDetailsProducts(booking)),
      giftcards: getGooglePaySummaryGiftcardItems(booking),
      fees: getGooglePaySummaryFeeItems(state.selectedPaymentMethod),
    };

    const context = { bookingRequestBody, bookingTrackingProps, successCallback, errorCallback, abortCallback };

    return {
      submitPayAtPlace: (turnstileToken: string) =>
        handleSubmitPayAtPlace(
          {
            ...context,
            fullyPaid: summary.finalPrice === 0,
            turnstileToken,
          },
          dispatch,
        ),
      submitAdyen: (options?: SubmitAdyenOptions) =>
        submitAdyenThroughWidgetPopup({
          widgetPayment,
          isBokadirektPay,
          paymentMethodType: state.selectedPaymentMethod.type,
          submitAdyen: () => {
            const method = mapSelectedPaymentMethod(state.selectedPaymentMethod.type);
            if (method) {
              const isStoredCard = state.selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.STORED_COF;
              bookingFlowTrackingSession.checkoutPaymentMethod(
                'initiated',
                method,
                isStoredCard ? { flush: true } : undefined,
              );
            }
            saveBookingCheckoutStateToLocalStorage({ ...state, formData });
            return handleSubmitAdyen(
              {
                ...context,
                selectedPaymentMethod: state.selectedPaymentMethod,
                bookState: { ...booking, employee: employee.selectedEmployeeId },
                walleyPhoneNumber: options?.walleyPhone,
                hasUser: Boolean(user),
                history,
                widgetPayment,
              },
              dispatch,
            );
          },
        }),
      submitCoF: handleSubmitCoF(context, dispatch),
      submitGuestCoF: handleSubmitGuestCoF(context, dispatch),
      submitThreeDS: handleSubmitThreeDSPayment({ ...context, cofThreeDS: state.cofThreeDS }, dispatch),
      submitGooglePay: (final) => {
        bookingFlowTrackingSession.checkoutPaymentMethod('initiated', 'google_pay');
        return handleSubmitGooglePay({ ...context, ...googlePaySummary }, dispatch)(final);
      },
      submitApplePay: (final) => {
        bookingFlowTrackingSession.checkoutPaymentMethod('initiated', 'apple_pay');
        return handleSubmitApplePay({ ...context, ...applePaySummary }, dispatch)(final);
      },
      submitSwish: (bookState) => {
        bookingFlowTrackingSession.checkoutPaymentMethod('initiated', 'swish', { flush: true });
        return handleSubmitSwish(
          {
            ...context,
            bookState: { ...bookState, employee: employee.selectedEmployeeId },
            swish: state.swish,
          },
          dispatch,
        );
      },
      initKlarna: () => handleInitKlarnaPayment(context, dispatch),
      initQliro: () => handleInitQliroPayment(context, dispatch),
    };
  };

  return {
    summary: state.summary,
    selectedPaymentMethod: state.selectedPaymentMethod,
    employee: employee,
    formFields: state.formFields,
    termsAccepted: state.termsAccepted,
    submitting: state.submitting,
    error: state.error,
    protectedEmailError: state.protectedEmailError,
    onlinePaymentRequiredEmail: state.onlinePaymentRequiredEmail,
    swish: state.swish,
    cofThreeDS: state.cofThreeDS,
    missingActions: state.missingActions,
    groupBooking: state.groupBooking,
    guestId: state.guestId,
    triggeredTurnstile: state.triggeredTurnstile,
    session: state.session,
    bookingId: state.bookingId,
    onTriggeredTurnstile: () => dispatch({ type: 'SET_TRIGGERED_TURNSTILE' }),
    onUpdateGroupBooking: handleUpdateGroupBooking,
    onDismissSwishQRCode: handleDismissSwishQRCode,
    onChangePaymentMethod: handleChangePaymentMethod,
    onApplyGiftcard: handleApplyGiftcard,
    onRemoveGiftcard: handleRemoveGiftcard,
    onRemoveBundle: handleRemoveBundle,
    onToggleTerms: handleToggleTerms,
    onCheckoutMissingAction: handleOnCheckoutMissingAction,
    onUseGuestCheckout: handleUseGuestCheckout,
    onValidateEmailProtected: handleOnValidateEmailProtected,
    onValidateOnlinePaymentRequiredEmail: handleOnValidateOnlinePaymentRequiredEmail,
    onDismissEmailProtectedError: handleDismissEmailProtectedError,
    bookingCheckoutAPI,
    onCloseAdyenCheckoutModal: handleCloseAdyenCheckoutModal,
    onResetPayment: () => dispatch({ type: 'RESET_PAYMENT' }),
    onSubmitting: () => dispatch({ type: 'SUBMITTING', payload: true }),
  };
};

const CheckoutContext = createContext<UseBookingCheckoutManagerResult>({
  employee: null,
  formFields: [],
  selectedPaymentMethod: { type: 0 },
  summary: null,
  submitting: false,
  error: undefined,
  protectedEmailError: false,
  termsAccepted: false,
  missingActions: [],
  groupBooking: null,
  bookingCheckoutAPI: () => ({
    submitPayAtPlace: async () => {},
    submitAdyen: async () => false,
    submitSwish: async () => {},
    submitCoF: async () => {},
    submitGuestCoF: async () => {},
    submitGooglePay: async () => {},
    submitApplePay: async () => {},
    submitThreeDS: async () => {},
    initKlarna: async () => {},
    initQliro: async () => {},
  }),
  onCloseAdyenCheckoutModal: () => {},
  onResetPayment: () => {},
  onSubmitting: () => {},
  onTriggeredTurnstile: () => {},
  onApplyGiftcard: () => () => {},
  onRemoveGiftcard: () => () => {},
  onRemoveBundle: () => () => {},
  onChangePaymentMethod: () => {},
  onToggleTerms: () => {},
  onDismissSwishQRCode: () => {},
  onCheckoutMissingAction: () => {},
  onUpdateGroupBooking: () => {},
  onUseGuestCheckout: () => {},
  onValidateEmailProtected: () => {},
  onValidateOnlinePaymentRequiredEmail: () => {},
  onDismissEmailProtectedError: () => {},
});

export const useBookingCheckout = () => {
  const context = useContext(CheckoutContext);

  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }

  return context;
};

export const BookingCheckoutProvider = ({
  children,
  cards = [],
  paymentHistory,
}: {
  children: ReactNode;
  cards?: PaymentCard[];
  paymentHistory: PaymentHistoryResponse;
}) => {
  const booking = useAppSelector((state) => state.book);
  const { formData } = useCheckoutFormData();
  return (
    <CheckoutContext.Provider value={useBookingCheckoutManager(booking, cards, formData, paymentHistory)}>
      {children}
    </CheckoutContext.Provider>
  );
};
